import { AdminRoles } from "@/config/adminRoles";

/**
 * Permisos por rol del portal. Espeja la matriz de src/user/permissions.ts del
 * monolito, que es la fuente de verdad: la API rechaza igual si acá se
 * habilita algo de más. Esto decide qué se muestra, no qué se puede.
 *
 * | Rol                        | Dashboards | Indicador interno | Descarga |
 * |----------------------------|------------|-------------------|----------|
 * | ADMIN                      | sí         | sí                | sí       |
 * | SUPERVISOR_MEDICO (UMA)    | sí         | sí                | sí       |
 * | SOPORTE                    | sí         | no                | sí       |
 * | OWNER                      | no         | no                | sí       |
 * | COLLABORATOR (enfermero)   | no         | no                | sí       |
 * | SUPERVISOR_OPERATIVO       | no         | no                | no       |
 * | SUPERVISOR (legacy)        | no         | no                | no       |
 */

/** Perfiles de UMA: los únicos que ven métricas que no le mostramos al cliente. */
export const INTERNAL_ROLES = [
	AdminRoles.ADMIN,
	AdminRoles.SUPERVISOR_MEDICO,
] as const;

/**
 * Administración de la operación: ve la performance por persona y el historial
 * de jornadas. Al cliente le alcanza con saber quién está disponible.
 */
export const ADMIN_DASHBOARD_ROLES = [AdminRoles.ADMIN] as const;

/** Quién entra a /monitoreo. */
export const SUPERVISION_DASHBOARD_ROLES = [
	AdminRoles.ADMIN,
	AdminRoles.SUPERVISOR_MEDICO,
	AdminRoles.SOPORTE,
] as const;

/**
 * Perfiles que no descargan el protocolo. Va como lista negativa porque el
 * pedido es ese: descargan todos menos el supervisor operativo.
 */
export const NO_PROTOCOL_DOWNLOAD_ROLES = [
	AdminRoles.SUPERVISOR_OPERATIVO,
	AdminRoles.SUPERVISOR,
] as const;

/** Los dos perfiles de supervisor: ninguno inicia evaluaciones. */
export const SUPERVISOR_ROLES = [
	AdminRoles.SUPERVISOR_OPERATIVO,
	AdminRoles.SUPERVISOR_MEDICO,
	AdminRoles.SUPERVISOR,
] as const;

type Roles = readonly { id: number }[] | null | undefined;

export function hasAnyRole(roles: Roles, allowed: readonly AdminRoles[]): boolean {
	if (!roles) return false;
	return roles.some((role) => allowed.includes(role.id));
}

/**
 * Quién puede reenviar el mail de baja de una evaluación.
 *
 * Mismo criterio que el reseteo de contraseña: el mail lleva el detalle
 * clínico de una persona y sale a destinatarios del cliente, así que no
 * alcanza con ver el listado. La API valida lo mismo.
 */
export function canResendBajaNotification(roles: Roles): boolean {
	if (!roles?.length) return false;
	return hasAnyRole(roles, [
		AdminRoles.ADMIN,
		AdminRoles.SOPORTE,
		// US 27658: supervisión médica también ve y reenvía las bajas.
		AdminRoles.SUPERVISOR_MEDICO,
	]);
}

/**
 * Quién puede cargar la matrícula de un profesional.
 *
 * Espeja **exactamente** `ALLOWED_ROLES` de `verifyCallerIsAdmin`, que es lo
 * que valida la API que la escribe (`PATCH /api/doctors/:uid/license`). Antes
 * la pantalla usaba "es ADMIN" a secas: un owner podía cargarla por API pero
 * no veía el campo, así que la matrícula del enfermero parecía no existir.
 */
export function canEditLicense(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.OWNER]);
}

/**
 * Quién da de alta perfiles médicos. Espeja `ALLOWED_ROLES` de
 * `verifyCallerIsAdmin`, que es lo que valida `POST /api/doctors/create`:
 * admin y owner. Antes el botón era sólo para ADMIN aunque la API ya aceptaba
 * al owner (pedido de operaciones, 1-oct).
 */
export function canCreateDoctor(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.OWNER]);
}

/**
 * Quién puede descartar una evaluación que quedó a medias.
 *
 * Admin y supervisor médico, el mismo grupo que corrige un resultado: una
 * evaluación trabada le bloquea el trabajo a un operador real, que es justo lo
 * que el supervisor médico supervisa.
 *
 * Soporte no entra —corregir un nombre mal cargado es soporte, decidir que un
 * control no cuenta no— y el enfermero menos. El backend valida lo mismo.
 */
export function canDiscardEvaluation(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_MEDICO]);
}

/**
 * Quién puede corregir el resultado de una evaluación cerrada.
 *
 * Admin y supervisor médico. El segundo es un perfil interno de UMA que ya ve
 * el resultado de todas las evaluaciones y descarga los protocolos: decidir si
 * una persona quedó apta es una cuestión clínica, y es más de su competencia
 * que de la de un administrador cualquiera.
 *
 * Soporte no, con el mismo criterio que en descartar: corregir un nombre mal
 * cargado es soporte, cambiar un dato de salud no.
 *
 * El backend valida lo mismo y además deja registrado quién lo hizo.
 */
export function canChangeApto(roles: Roles): boolean {
	// Owner: el médico coordinador del cliente cambia resultados con su usuario.
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_MEDICO, AdminRoles.OWNER]);
}

/**
 * Quién puede anular un protocolo ya emitido.
 *
 * Admin y owner (pedido 27007), a diferencia de descartar o cambiar el
 * resultado: anular saca de circulación un apto que ya se entregó, no es
 * reversible y no es una decisión clínica sino administrativa —el caso típico
 * es un apto cargado en el legajo equivocado—. El backend valida lo mismo.
 */
export function canAnnulProtocol(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.OWNER]);
}

/**
 * Quién administra el padrón de etilómetros: alta, corrección y vencimiento
 * de la calibración.
 *
 * Admin y owner, el mismo corte que `SoloAdminGuard` en el backend, que es
 * quien decide de verdad. El enfermero sigue pudiendo dar de alta un equipo
 * desde la atención, pero no corregir los que ya están.
 */
export function canManageBreathalyzers(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.OWNER]);
}

/**
 * Quién puede corregir la base / CRPC de una atención registrada.
 *
 * **Sólo administrador**, y el corte es más estricto que el del apto a
 * propósito: el resultado es una cuestión clínica y por eso entra el supervisor
 * médico, pero mover una atención de base mueve los números de las dos —en el
 * tablero, en la exportación y en los conteos por línea, también de los meses
 * ya cerrados—. Eso es administración, no clínica. El ticket 26452 lo
 * restringe explícitamente.
 *
 * El backend valida lo mismo y deja registrado quién la movió y por qué.
 */
export function canChangeOperationBase(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN]);
}

/**
 * Quién puede bajarse el detalle clínico de las atenciones.
 *
 * Es la historia clínica de cada persona en un archivo, así que el corte es más
 * estricto que el de ver el tablero: el supervisor operativo no entra —hoy ni
 * siquiera puede descargar un protocolo suelto— y el enfermero tampoco. El
 * backend valida lo mismo.
 */
export function canExportAtenciones(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_MEDICO]);
}

/**
 * Quién entra a la sección SIGOF: estado de la integración con SIGOF2 y
 * reintento manual de envíos. ADMIN (UMA) y el supervisor operativo, que la
 * usa para monitorear qué evaluaciones no llegaron a SIGOF2. Los dos ven todas
 * las líneas y pueden reintentar. OWNER no entra. El backend valida lo mismo
 * (`SeguimientoDeSigofGuard`).
 */
export function canSeeSigof(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_OPERATIVO]);
}

/** Ve el dashboard completo, con performance e historial por persona. */
export function esAdministracion(roles: Roles): boolean {
	return hasAnyRole(roles, ADMIN_DASHBOARD_ROLES);
}

/**
 * Quién entra a "Indicadores por línea". Espeja la API: administración ve
 * todas las líneas y la tabla por enfermero; el supervisor de línea, sólo las
 * suyas. Qué línea puede pedir cada uno lo decide la API, no esto.
 */
export const INDICADORES_POR_LINEA_ROLES = [
	AdminRoles.ADMIN,
	AdminRoles.SUPERVISOR_LINEA,
] as const;

export function canSeeIndicadoresPorLinea(roles: Roles): boolean {
	return hasAnyRole(roles, INDICADORES_POR_LINEA_ROLES);
}

/**
 * Quien es supervisor de línea y nada más: no tiene nada que hacer en
 * Operaciones, así que al entrar al portal va directo a sus indicadores.
 */
export function esSoloSupervisorDeLinea(roles: Roles): boolean {
	if (!roles || roles.length === 0) return false;
	return roles.every((rol) => rol.id === AdminRoles.SUPERVISOR_LINEA);
}

/**
 * Perfiles que ven sólo las atenciones de sus líneas (US 27422). Espeja
 * `tieneAlcancePorLinea` de la API: vale si **todos** sus roles son de esta
 * lista. Acá sólo ordena la pantalla; la restricción la aplica la API.
 */
export const ROLES_CON_ALCANCE_POR_LINEA = [
	AdminRoles.SUPERVISOR_OPERATIVO,
	AdminRoles.SUPERVISOR_LINEA,
] as const;

export function tieneAlcancePorLinea(roles: Roles): boolean {
	if (!roles || roles.length === 0) return false;
	return roles.every((rol) =>
		(ROLES_CON_ALCANCE_POR_LINEA as readonly number[]).includes(rol.id),
	);
}

/** Quién recibe líneas en Administración → Usuarios. */
export function puedeTenerLineas(roles: Roles): boolean {
	return hasAnyRole(roles, ROLES_CON_ALCANCE_POR_LINEA);
}

/** Dashboards de supervisión y métricas de gestión. */
export function canAccessSupervision(roles: Roles): boolean {
	return hasAnyRole(roles, SUPERVISION_DASHBOARD_ROLES);
}

/**
 * Indicadores que no van al cliente. Hoy: cuántos médicos están conectados en
 * cada momento. La API además no los devuelve si el rol no corresponde.
 */
export function canSeeInternalIndicators(roles: Roles): boolean {
	return hasAnyRole(roles, INTERNAL_ROLES);
}

/**
 * Descarga del protocolo, el Excel de evaluaciones y la impresión del día.
 *
 * Ojo con el alcance: el PDF se arma en el navegador con el mismo detalle de
 * la atención que el supervisor operativo necesita para validar el apto. Esto
 * esconde el botón, no corta los datos.
 *
 * **Sin roles no se descarga.** Es la única de estas funciones que falla
 * cerrada, y va al revés que el resto a propósito: las demás son listas
 * negativas, así que un perfil desconocido las pasaba. `/user/me` devuelve 401
 * cada vez que vence el token de la cookie —pasa seguido—, y con eso el
 * supervisor operativo volvía a ver el botón de descargar el protocolo.
 *
 * Que un admin pierda la descarga por un token vencido se arregla recargando.
 * Al revés no: el protocolo ya se descargó.
 */
export function canDownloadProtocol(roles: Roles): boolean {
	if (!roles?.length) return false;
	return !hasAnyRole(roles, NO_PROTOCOL_DOWNLOAD_ROLES);
}

/**
 * El perfil operativo: jefe de estación. Valida el apto con el troquel, sin
 * descargar el protocolo y sin entrar a los pasos del protocolo, que son
 * editables. Incluye el rol 3, que quedó como legacy del mismo perfil.
 */
export function isOperationalSupervisor(roles: Roles): boolean {
	return hasAnyRole(roles, NO_PROTOCOL_DOWNLOAD_ROLES);
}

/** El supervisor operativo valida atenciones ajenas; no inicia una nueva. */
export function canCreateEvaluation(roles: Roles): boolean {
	return !hasAnyRole(roles, SUPERVISOR_ROLES);
}

/** Los perfiles de supervisor no administran usuarios ni bases. */
export function isSupervisorProfile(roles: Roles): boolean {
	return hasAnyRole(roles, SUPERVISOR_ROLES);
}

/**
 * Quién configura el cupo de PSA y ve el seguimiento: admin y supervisor
 * médico. Los listados llevan nombre, función y estado de PSA de cada persona,
 * y el historial el resultado, que es un dato de salud. El backend valida lo
 * mismo.
 */
export function canManagePsa(roles: Roles): boolean {
	return hasAnyRole(roles, [AdminRoles.ADMIN, AdminRoles.SUPERVISOR_MEDICO]);
}
